import axiosInstance, { getNewCancelToken } from '@/services/_AxiosInstance';

const urlBase = '/master/Rpm_Operation_Header/';

/**
 * @typedef {Object} RpmOperationHeaderPayload
 * @property {number} mrohCompId - ID company.
 * @property {number} mrohMassId - ID asset/vessel.
 * @property {number} mrohDevcId - ID device RPM.
 * @property {string} mrohStartTime - Format datetime `YYYY-MM-DD HH:mm:ss`.
 * @property {number} mrohStatus - Status aktif/non-aktif (umumnya 1/0).
 */

/**
 * @typedef {Object} RpmOperationHeaderItem
 * @property {number} mrohId
 * @property {number|null} mrohCompId
 * @property {number|null} mrohMassId
 * @property {number|null} mrohDevcId
 * @property {string} mrohStartTime
 * @property {number} mrohStatus
 * @property {string} mrohCreatedUserId
 * @property {string} mrohCreatedTime
 * @property {string} mrohUpdatedUserId
 * @property {string} mrohUpdatedTime
 * @property {Object} [mrohComp] - Data relasi company.
 * @property {Object} [mrohMass] - Data relasi asset.
 * @property {Object} [mrohDevc] - Data relasi device.
 */

/**
 * @typedef {Object} RpmOperationHeaderListResponse
 * @property {Object<string, Object>} fields - Metadata field dari API.
 * @property {RpmOperationHeaderItem[]} data - List data operation header.
 * @property {Object} pagination - Informasi pagination.
 * @property {Object} status - Status response API.
 */

/**
 * @typedef {Object} RpmOperationHeaderCreateResponse
 * @property {number} id - ID record yang berhasil dibuat.
 * @property {Object} status - Status response API.
 */

/**
 * @typedef {Object} RpmOperationHeaderMutationResponse
 * @property {number} affected_rows - Jumlah data yang terpengaruh.
 * @property {Object} status - Status response API.
 */

/**
 * Service untuk endpoint Master RPM Operation Header.
 * @namespace RpmOperationHeaderSvc
 */
export default {
  /**
   * Ambil list RPM Operation Header.
   * @function getRpmOperationHeaders
   * @param {Object} [params] - Query params (contoh: `page`, `max`, `findField`, `findValue`).
   * @returns {Promise<RpmOperationHeaderListResponse>}
   */
  getRpmOperationHeaders: async (params = {}) => {
    const { data } = await axiosInstance.get(urlBase, {
      params,
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Ambil RPM Operation Header berdasarkan ID.
   * @function getRpmOperationHeaderById
   * @param {number} id - ID data.
   * @param {Object} [params] - Query params.
   * @returns {Promise<RpmOperationHeaderItem>}
   */
  getRpmOperationHeaderById: async (id, params = {}) => {
    const { data } = await axiosInstance.get(`${urlBase}${id}`, {
      params,
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Buat data RPM Operation Header baru.
   * @function createRpmOperationHeader
   * @param {RpmOperationHeaderPayload} payload - Body request create.
   * @returns {Promise<RpmOperationHeaderCreateResponse>}
   */
  createRpmOperationHeader: async (payload) => {
    const { data } = await axiosInstance.post(urlBase, payload, {
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Update data RPM Operation Header berdasarkan ID.
   * @function updateRpmOperationHeader
   * @param {number} id - ID data yang akan diupdate.
   * @param {RpmOperationHeaderPayload} payload - Body request update.
   * @returns {Promise<RpmOperationHeaderMutationResponse>}
   */
  updateRpmOperationHeader: async (id, payload) => {
    const { data } = await axiosInstance.put(`${urlBase}${id}`, payload, {
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Hapus data RPM Operation Header berdasarkan ID.
   * @function deleteRpmOperationHeader
   * @param {number} id - ID data yang akan dihapus.
   * @returns {Promise<RpmOperationHeaderMutationResponse>}
   */
  deleteRpmOperationHeader: async (id) => {
    const { data } = await axiosInstance.delete(`${urlBase}${id}`, {
      cancelToken: getNewCancelToken(),
    });
    return data;
  },
};
